import { Layer, TemplateData } from '../types/design';
import { getEffectiveKeyframes } from './animationUtils';


/**
 * Compiles the complex Editor state into a flat, player-ready JSON
 */
export const compilePlayerData = (editorData: TemplateData) => {
    // Determine base dimensions for normalization
    const baseWidth = editorData.width || editorData.canvasDimensions?.width || 1920;
    const baseHeight = editorData.height || editorData.canvasDimensions?.height || 1080;

    /**
     * Converts pixels to percentages relative to design dimensions
     */
    const pxToPercentX = (px: number) => (px / baseWidth) * 100;
    const pxToPercentY = (px: number) => (px / baseHeight) * 100;


    const processPage = (pageData: any) => {
        const {
            dynamicLayers = [],
            layerPositions = {},
            textStyles = {},
            imageSettings = {},
            videoSettings = {},
            shapeSettings = {},
            textContents = {},
            scrollerSettings = {},
            widgetSettings = {},
            backgroundSettings = {}
        } = pageData;

        const compileLayer = (layer: Layer): any => {
            const pos = layerPositions[layer.id] || {};
            const textStyle = textStyles[layer.id] || {};
            const imgSetting = imageSettings[layer.id] || {};
            const videoSetting = videoSettings[layer.id] || {};
            const shapeSetting = shapeSettings[layer.id] || {};

            // Base properties (normalized to percentages)
            const compiled: any = {
                id: layer.id,
                type: layer.type,
                name: layer.name,
                baseStyle: {
                    x: pxToPercentX(pos.left ?? 0),
                    y: pxToPercentY(pos.top ?? 0),
                    w: pxToPercentX(pos.width ?? 0),
                    h: pxToPercentY(pos.height ?? 0),
                    rotation: pos.rotation || 0,
                    opacity: (layer.opacity ?? 100) / 100,
                }
            };

            // Type-specific content and style flattening
            if (layer.type === 'text') {
                compiled.content = textContents[layer.id] || layer.content || '';
                compiled.style = {
                    fontSize: `${(textStyle.fontSize || 16) / baseHeight * 100}vh`,
                    color: textStyle.color || '#000000',
                    fontFamily: textStyle.fontFamily,
                    fontWeight: textStyle.fontWeight,
                    textAlign: textStyle.textAlign,
                    lineHeight: textStyle.lineHeight,
                    letterSpacing: textStyle.letterSpacing,
                };
            } else if (layer.type === 'image') {
                compiled.content = imgSetting.url || layer.content || '';
                compiled.assetId = imgSetting.assetId || layer.assetId;
                compiled.style = {
                    objectFit: imgSetting.objectFit || 'cover',
                    borderRadius: imgSetting.borderRadius,
                };
            } else if (layer.type === 'video') {
                compiled.content = videoSetting.url || layer.content || '';
                compiled.assetId = videoSetting.assetId || layer.assetId;
                compiled.style = {
                    objectFit: videoSetting.objectFit || 'cover',
                    borderRadius: videoSetting.borderRadius,
                    muted: videoSetting.muted,
                    loop: videoSetting.loop,
                    autoplay: videoSetting.autoplay,
                    volume: videoSetting.volume ?? 100,
                };
            } else if (layer.type === 'shape' as any || layer.type === 'rectangle') {
                compiled.content = layer.content; // rectangle/circle
                compiled.style = {
                    fill: shapeSetting.fill?.color,
                    stroke: shapeSetting.stroke?.color,
                    strokeWidth: shapeSetting.stroke?.width,
                    cornerRadius: shapeSetting.cornerRadius,
                };
            } else if (layer.type === 'playlist') {
                compiled.content = layer.content;
            } else if (layer.isApp) {
                compiled.isApp = true;
                if (widgetSettings[layer.id]) {
                    compiled.widgetSettings = widgetSettings[layer.id];
                } else if (scrollerSettings[layer.id]) {
                    // Fallback for legacy scroller settings if needed, 
                    // but usually scroller is handled by its own component in player if isApp is true
                    compiled.scrollerSettings = scrollerSettings[layer.id];
                }
            }

            // Dynamic variable mapping metadata preservation
            const binding = textStyle?.binding || imgSetting?.binding || videoSetting?.binding || shapeSetting?.binding;
            if (binding?.customApiId && binding?.apiKey) {
                compiled.binding = {
                    customApiId: binding.customApiId,
                    apiKey: binding.apiKey,
                    fallbackValue: binding.fallbackValue
                };
            }

            // Animation Baking (Property Tracks)
            const animation = textStyle.animation || imgSetting.animation || videoSetting.animation || shapeSetting.animation || layer.animation;
            if (animation && (typeof animation === 'string' ? animation !== 'none' : animation.type !== 'none')) {
                const animSettings = typeof animation === 'string'
                    ? { type: animation, duration: 1000, delay: 0, easing: 'ease', loop: false }
                    : animation;

                const keyframes = getEffectiveKeyframes(animSettings as any);
                compiled.tracks = {};

                // Convert Keyframe[] to Property Tracks
                keyframes.forEach((kf: any) => {
                    Object.entries(kf.properties).forEach(([prop, val]) => {
                        if (!compiled.tracks[prop]) compiled.tracks[prop] = [];

                        let bakedVal = val;
                        // Normalize positional animation values
                        if (prop === 'x') bakedVal = pxToPercentX(val as number);
                        if (prop === 'y') bakedVal = pxToPercentY(val as number);

                        compiled.tracks[prop].push({
                            t: kf.time + (animSettings.delay || 0),
                            v: bakedVal
                        });
                    });
                });
            }

            if (layer.children) {
                compiled.children = layer.children.map(compileLayer);
            }

            return compiled;
        };

        return {
            id: pageData.id || `page_${Math.random().toString(36).substring(2, 9)}`,
            name: pageData.name || 'Untitled Page',
            background: backgroundSettings?.['root'] || {},
            layers: dynamicLayers.map(compileLayer)
        };
    };

    return {
        version: '2.0-player',
        metadata: {
            name: editorData.name || 'Untitled Design',
            duration: 20000,
            width: baseWidth,
            height: baseHeight,
            aspectRatio: baseWidth / baseHeight,
            orientation: baseWidth >= baseHeight ? 'landscape' : 'portrait'
        },
        pages: (editorData.pages && editorData.pages.length > 0)
            ? editorData.pages.map(processPage)
            : [processPage(editorData)] // Fallback to current state if no pages array
    };
};

/**
 * Extracts all downloadable media URLs from a compiled player_data object.
 *
 * The Android Player uses this flat list as a "download queue" before playback.
 * Each S3 URL here must be downloaded and stored locally.
 * The local paths are then passed back to the web player via the event bridge
 * as `local_asset_map` { s3Url: localFilePath }.
 *
 * Apps/widgets are intentionally excluded — they load as iframes and
 * handle their own browser-level caching.
 *
 * @param playerData - The compiled player_data JSON object
 * @returns Deduplicated flat string[] of S3 media URLs to download offline
 */
/**
 * Extracts all downloadable media assets from a compiled player_data object.
 *
 * Returns an array of objects containing the S3 URL and its unique assetId (if available).
 * The Android Player uses this for its download queue and local path mapping.
 *
 * @param playerData - The compiled player_data JSON object
 * @returns Deduplicated array of { url: string, assetId?: string }
 */
export const extractOfflineAssets = (playerData: any): any[] => {
    const assetsMap = new Map<string, { url: string, assetId?: string }>();

    const isValidMediaUrl = (url: any): boolean => {
        if (!url || typeof url !== 'string') return false;
        if (!url.startsWith('http')) return false;
        
        // Exclude specific platform URLs that aren't user media
        const lower = url.toLowerCase();
        if (lower.includes('api.wejha.com')) return false;
        if (lower.includes('google-analytics')) return false;

        const hasMediaExt = (
            lower.includes('.jpg') || lower.includes('.jpeg') ||
            lower.includes('.png') || lower.includes('.gif') ||
            lower.includes('.webp') || lower.includes('.mp4') ||
            lower.includes('.webm') || lower.includes('.mov') ||
            lower.includes('.svg')
        );

        const isKnownBucket = (
            lower.includes('user-content') ||
            lower.includes('template-background-image') ||
            lower.includes('signage-cms')
        );

        return hasMediaExt || isKnownBucket;
    };

    const extractIdFromUrl = (url: string): string => {
        try {
            const cleanUrl = url.split('?')[0];
            const filename = cleanUrl.split('/').pop() || '';
            // Remove extension to get a stable ID
            return filename.split('.')[0] || filename;
        } catch {
            return url;
        }
    };

    const addAsset = (url: string, assetId?: string) => {
        if (!isValidMediaUrl(url)) return;
        
        // Strip query parameters to get a clean S3 URL (without response-content-disposition, signatures, etc.)
        const cleanUrl = url.split('?')[0];
        
        // Ensure we ALWAYS have an assetId for the player mapping
        const finalId = assetId || extractIdFromUrl(cleanUrl);
        
        // Deduplicate: If we already have this URL but it lacks an assetId, 
        // and now we found one with an assetId, update it.
        const existing = assetsMap.get(cleanUrl);
        if (!existing || (!existing.assetId && finalId)) {
            assetsMap.set(cleanUrl, { url: cleanUrl, assetId: finalId });
        }
    };

    const walkLayer = (layer: any) => {
        if (!layer) return;

        if ((layer.type === 'image' || layer.type === 'video') && layer.content) {
            addAsset(layer.content, layer.assetId);
        }

        if (layer.type === 'playlist' && layer.content) {
            try {
                const playlistData = typeof layer.content === 'string'
                    ? JSON.parse(layer.content)
                    : layer.content;

                (playlistData.contents || []).forEach((item: any) => {
                    const mediaUrl = item.signedUrl || item.url;
                    if (mediaUrl) {
                        addAsset(mediaUrl, item.assetId || item._id || item.id);
                    }
                });
            } catch {
                // ignore malformed playlist content
            }
        }

        if (Array.isArray(layer.children)) {
            layer.children.forEach(walkLayer);
        }
    };

    (playerData?.pages || []).forEach((page: any) => {
        // BACKGROUND CHECK: Capture page-level background images
        if (page.background?.type === 'image' && page.background.imageUrl) {
            addAsset(page.background.imageUrl, page.background.assetId);
        }

        (page.layers || []).forEach(walkLayer);
    });

    return Array.from(assetsMap.values());
};
